import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { DataGrid } from '@mui/x-data-grid';
import { updateResearchTaskStatus } from '../../api';
import { defaultPaginationOptions } from './utils';
import ContentModal from './ContentModal';
import CommentsList from './CommentsList';

import '../Dashboard/styles.scss';
import './styles.scss';

/**
 * TaskGrid Component
 */
export default function TaskGrid({
    taskData,
    setTaskData,
    editMode,
    page,
    setPage,
    pageSize,
    setPageSize,
    setSortOrder,
    setSortField,
    rowCount,
    selectionModel,
    onSelect,
}) {
    const [commentsValue, setCommentsValue] = useState('');
    const [commentOpen, setCommentOpen] = useState(false);
    const [taskColumnVisibilityModel, setTaskColumnVisibilityModel] = useState({
        zone: false,
        requiredSegments: false,
        submittedSegments: false,
        id: false,
    });
    const [snackbarData, setSnackbarData] = useState({
        status: 'success',
        text: '',
    });
    const [statusSnackbar, setStatusSnackbar] = useState(false);

    // List of possible page sizes (10, 25, 50, etc)
    // Only set pageSize to one of these allowed values
    // const allowedPageSizes = generatePaginationOptions(taskData.length);
    const allowedPageSizes = defaultPaginationOptions;

    // When user goes to next or prev page, re-fetch project location-tasks
    const onPageChange = async (newPage) => {
        console.log('TaskGrid: onPageChange', newPage);
        setPage(newPage);
    };

    // When user changes page size, re-fetch project location-tasks
    const onPageSizeChange = async (newPageSize) => {
        console.log('TaskGrid: onPageSizeChange', newPageSize);
        setPage(0);
        setPageSize(newPageSize);
    };

    // data grid sorting server call
    const handleSortModelChange = async (sortModel) => {
        const sortOrder =
            sortModel && Array.isArray(sortModel) && sortModel.length === 1
                ? sortModel[0].sort.toUpperCase()
                : null;
        setSortOrder(sortOrder);
        const sortField =
            sortModel && Array.isArray(sortModel) && sortModel.length === 1
                ? sortModel[0].field
                : null;
        setSortField(sortField);
    };

    /**
     * Closes an open content modal and resets values back to defaults
     */
    const handleContentClose = () => {
        setCommentOpen(false);
        setCommentsValue(null);
    };

    // Sets the value of the status dropdown for the selected row
    const handleTaskStatusChange = (params, status) => {
        const taskId = params.row.id;
        console.log('#### handleTaskStatusChange', taskId, status);

        // call API to update Segment status
        updateTaskStatus(taskId, status);

        // Update this Task status in the state
        const taskDataWithNewStatus = taskData.map((task) => {
            if (task.id === params.row.id) {
                return { ...params.row, status };
            } else {
                return task;
            }
        });
        setTaskData(taskDataWithNewStatus);

        // Does returning a value have any effect?
        // return { ...params.row, status };
    };

    const updateTaskStatus = async (taskId, nextStatus) => {
        try {
            const updateStatusResult = await updateResearchTaskStatus(
                taskId,
                nextStatus,
            );

            console.log('updateded status result', updateStatusResult);

            // provide a success or failure snackbar message based on result
            setStatusSnackbar(true);
            if (updateStatusResult) {
                console.log(updateStatusResult, 'update result');

                setSnackbarData({
                    status: 'success',
                    text: 'Successfully updated task status!',
                });
            } else {
                setSnackbarData({
                    status: 'error',
                    text: 'Failed to update task status',
                });
            }
        } catch (err) {
            setStatusSnackbar(true);
            setSnackbarData({
                status: 'error',
                text: 'Failed to update task status',
            });
        }
        // finally {
        //     // mark pending update to trigger refetch
        //     setPendingUpdate(true);
        // }
    };

    const locationColumns = [
        {
            field: 'locationName',
            headerName: 'Location Name',
            width: 220,
            type: 'string',
            sortable: true,
            renderCell: (params) => {
                return (
                    <abbr title={params.row.locationName}>
                        {params.row.locationName}
                    </abbr>
                );
            },
        },
        {
            field: 'taskName',
            headerName: 'Task Name',
            width: 220,
            type: 'string',
            sortable: true,
            renderCell: (params) => {
                return (
                    <abbr title={params.row.taskName}>
                        {params.row.taskName}
                    </abbr>
                );
            },
        },
        {
            field: 'zone',
            headerName: 'Zone',
            width: 50,
            type: 'number',
            sortable: true,
        },
        {
            field: 'updatedAt',
            headerName: 'Updated At',
            width: 240,
            type: 'date',
            sortable: true,
        },
        {
            field: 'assignedResearcher',
            headerName: 'Assigned Researcher',
            width: 160,
            type: 'string',
            sortable: true,
            description: "Researcher assigned to this 'LocationTask'",
        },
        {
            field: 'submittedSegments',
            headerName: 'Submitted Segments',
            type: 'number',
            width: 150,
            sortable: true,
        },
        {
            field: 'requiredSegments',
            headerName: 'Required Segments',
            type: 'number',
            width: 150,
            sortable: false,
        },
        {
            field: 'acceptedSegments',
            headerName: 'Accepted Segments',
            type: 'number',
            width: 150,
            sortable: false,
        },
        {
            field: 'rejectedSegments',
            headerName: 'Rejected Segments',
            type: 'number',
            width: 150,
            sortable: false,
        },
        {
            field: 'commentsMessage',
            headerName: 'Comments',
            width: 130,
            type: 'string',
            renderCell: (params) => {
                if (params.row.comments && params.row.comments.length > 0) {
                    return (
                        <button
                            onClick={(event) => {
                                event.stopPropagation(); // Required to prevent handleTaskSelection

                                setCommentsValue(params.row.comments);
                                setCommentOpen(true);
                            }}
                        >
                            {params.row.commentsMessage}
                        </button>
                    );
                } else return '';
            },
            sortable: false,
        },
        // {
        //     field: 'binPhotos',
        //     headerName: 'Bin Photo(s)',
        //     width: 120,
        //     type: 'number',
        //     sortable: false,
        // },
        // {
        //     field: 'litterPhotos',
        //     headerName: 'Litter Photo(s)',
        //     width: 120,
        //     type: 'number',
        //     sortable: false,
        // },
        // {
        //     field: 'hotspotPhotos',
        //     headerName: 'Hotspot Photo(s)',
        //     width: 130,
        //     type: 'number',
        //     sortable: false,
        // },
        {
            field: 'id',
            headerName: 'Id',
            width: 50,
            type: 'number',
            sortable: true,
        },
    ];

    // Default status column definition
    let statusColumn = {
        field: 'status',
        headerName: 'Statuses',
        description: 'Enable edit mode to update a task status',
        width: 120,
        type: 'string',
        sortable: true,
        editable: true,
        renderCell: (params) => {
            return (
                <select
                    name="status"
                    value={params.row.status}
                    disabled={!editMode}
                    onChange={(e) =>
                        handleTaskStatusChange(params, e.target.value)
                    }
                >
                    <option value="SUBMITTED" disabled>
                        Submitted
                    </option>
                    <option value="ACCEPTED">Accepted</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="IN_PROGRESS" disabled>
                        In Progress
                    </option>
                    <option value="UNASSIGNED" disabled>
                        Unassigned
                    </option>
                    <option value="ASSIGNED" disabled>
                        Assigned
                    </option>
                    <option value="SKIPPED" disabled>
                        Skipped
                    </option>
                </select>
            );
        },
    };

    // In edit mode, append styling to task status column
    if (editMode) {
        statusColumn.headerName = 'Edit Status';
        statusColumn.headerClassName = 'task-grid-status--header';
        statusColumn.cellClassName = 'task-grid-status--cell';
    }

    // Append task status column to data grid
    locationColumns.push(statusColumn);

    return (
        <div className="col-12" style={{ height: '650px' }}>
            <DataGrid
                density="compact"
                // loading={isLoading} // Consider passing in isLoading from parent, showing the DataGrid while fetching data
                disableColumnFilter
                initialState={{
                    sorting: {
                        sortModel: [{ field: 'locationName', sort: 'asc' }],
                    },
                    pagination: {
                        pageSize: rowCount || 0,
                    },
                }}
                columnVisibilityModel={taskColumnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                    setTaskColumnVisibilityModel(newModel)
                }
                style={{ backgroundColor: 'white' }}
                rows={taskData}
                columns={locationColumns}
                getRowId={(row) => row.id}
                paginationMode="server"
                rowCount={rowCount}
                rowsPerPageOptions={allowedPageSizes}
                pageSize={
                    allowedPageSizes.includes(pageSize) ? pageSize : undefined
                }
                onPageSizeChange={onPageSizeChange}
                page={page}
                onPageChange={onPageChange}
                checkboxSelection={true}
                disableSelectionOnClick={editMode}
                selectionModel={selectionModel}
                onSelectionModelChange={onSelect}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
            />
            {/* Popup to display comments */}
            <ContentModal open={commentOpen} handleClose={handleContentClose}>
                <CommentsList comments={commentsValue} />
            </ContentModal>

            {/* Snackbar pop up for update task status feedback */}
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                open={statusSnackbar}
                autoHideDuration={6000}
                onClose={() => setStatusSnackbar(false)}
            >
                <Alert
                    onClose={() => setStatusSnackbar(false)}
                    severity={snackbarData.status || 'success'}
                    sx={{ width: '100%' }}
                >
                    {snackbarData.text || ''}
                </Alert>
            </Snackbar>
        </div>
    );
}

TaskGrid.propTypes = {
    taskData: PropTypes.array,
    setTaskData: PropTypes.func,
    editMode: PropTypes.bool,
    page: PropTypes.number,
    setPage: PropTypes.func,
    pageSize: PropTypes.number,
    setPageSize: PropTypes.func,
    setSortOrder: PropTypes.func,
    setSortField: PropTypes.func,
    rowCount: PropTypes.number,
    selectionModel: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array,
    ]),
    onSelect: PropTypes.func,
};
